import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SessionType } from "@/types"

import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

type UpdateSessionProjectPayload = {
  sessionId: string
  projectId: string
}

function updateSessionProject({ sessionId, projectId }: UpdateSessionProjectPayload) {
  return api.put("/update-session", { session_id: sessionId, project_id: projectId })
}

type UseUpdateSessionProjectArgs = {
  sessionType: SessionType
  onSuccess?: () => void
}

export function useUpdateSessionProject({ sessionType, onSuccess }: UseUpdateSessionProjectArgs) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const updateSessionProjectMutation = useMutation({
    mutationKey: queryKeys.updateSessionProject,
    mutationFn: updateSessionProject,
    onSuccess: async () => {
      // Invalidate all session queries to refresh the lists
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })

      // Also invalidate sessions query without projectId filter to refresh main list
      await queryClient.invalidateQueries({
        queryKey: ["sessions"],
      })

      toast.success(
        t("chatsession.move_to_project_success", { defaultValue: "Chat moved to project" })
      )

      onSuccess?.()
    },
    onError: (error) => {
      logger.error("Failed to move chat to project", error as Error)
      toast.error(
        t("chatsession.move_to_project_error", { defaultValue: "Unable to move chat to project" })
      )
    },
  })

  const moveToProject = async (sessionId: string, projectId: string) => {
    try {
      await updateSessionProjectMutation.mutateAsync({
        sessionId,
        projectId,
      })
    } catch (error) {
      logger.debug("Move to project mutation rejected", error as Error)
    }
  }

  return {
    moveToProject,
    updateSessionProjectMutation,
  }
}
